import { PinInput as BasePinInput, PinInputProps } from '@chakra-ui/pin-input';
import { Interpolation } from '@emotion/react';
import { Theme } from '@lbcamden/lib-react';
import { FunctionComponent, useRef } from 'react';

import { userPinLength } from '~/common/schemas/auth';

import { DigitInput, PinInputRoot } from './PinInput.styled';

type Props = Omit<PinInputProps, 'children'> & {
  css?: Interpolation<Theme>;
  className?: string;
};

const inputLabel = (index: number) => `PIN digit ${index + 1}`;

export const PinInput: FunctionComponent<Props> = ({ css, className, ...props }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // Ensure focus is always on the input for the last entered or next digit
  const onFocusInput = (index: number, target: HTMLInputElement) => {
    if (!rootRef.current) {
      return;
    }
    const pinValue = props.value ?? '';
    const lastEnteredDigitIndex = pinValue.length - 1;
    const nextDigitIndex = Math.min(lastEnteredDigitIndex + 1, userPinLength - 1);

    if (![lastEnteredDigitIndex, nextDigitIndex].includes(index)) {
      const nextInput = rootRef.current.querySelector<HTMLInputElement>(
        `[data-index="${nextDigitIndex}"]`,
      );
      nextInput?.focus();
      nextInput?.setSelectionRange(1, 1);
    } else {
      requestAnimationFrame(() => target.setSelectionRange(1, 1));
    }
  };

  return (
    <PinInputRoot ref={rootRef} css={css} className={className}>
      <BasePinInput mask type="number" placeholder="" {...props}>
        {Array.from({ length: userPinLength }, (_v, index) => (
          <DigitInput
            key={index}
            data-index={index}
            data-testid={inputLabel(index)}
            aria-label={inputLabel(index)}
            size={1}
            onFocus={({ target }) => onFocusInput(index, target)}
          />
        ))}
      </BasePinInput>
    </PinInputRoot>
  );
};
